import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTimePeriod, TimezonePreferences, DEFAULT_TIMEZONE_PREFERENCES } from '../utils/timezone';
import persistenceService from '../utils/persistenceService';
import ErrorBoundary from './ErrorBoundary';
import { useToast } from '../hooks/useToast';
import MorningCheckIn from './MorningCheckIn';
import EveningCheckIn from './EveningCheckIn';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface DailyCheckInStatus {
  date: string;
  morning: boolean;
  evening: boolean;
  completionStatus: 'both' | 'morning-only' | 'evening-only' | 'none';
}

const CheckInPrompt: React.FC = () => {
  const [checkInStatus, setCheckInStatus] = useState<DailyCheckInStatus | null>(null);
  const [timezonePreferences, setTimezonePreferences] = useState<TimezonePreferences>(DEFAULT_TIMEZONE_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [currentCheckIn, setCurrentCheckIn] = useState<'morning' | 'evening' | null>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Load today's check-in status
  useEffect(() => {
    const loadCheckInStatus = async () => {
      try {
        setLoading(true);
        
        // Load timezone preferences
        const preferences = await persistenceService.getPreferences();
        if (preferences.timezone) {
          setTimezonePreferences(preferences.timezone);
        }

        // Fetch today's check-in status
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/signup');
          return;
        }

        const headers: Record<string, string> = {
          'x-auth-token': token
        };
        
        // Add access granted header if password protection was bypassed
        const accessGranted = localStorage.getItem('thinkmate_access_granted');
        if (accessGranted === 'true') {
          headers['x-access-granted'] = 'true';
        }

        const response = await fetch(`${API_URL}/api/daily-checkins/today`, {
          headers
        });

        if (!response.ok) {
          throw new Error('Failed to fetch check-in status');
        }

        const data = await response.json();
        setCheckInStatus({
          date: data.date,
          morning: !!data.morning,
          evening: !!data.evening,
          completionStatus: data.completionStatus || 'none'
        });

      } catch (error) {
        console.error('Error loading check-in status:', error);
        showToast('Failed to load check-in status', 'error');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadCheckInStatus();
  }, [navigate, showToast]);

  // Determine which check-in to show based on time and completion status
  const getRecommendedCheckIn = (): 'morning' | 'evening' | 'complete' => {
    if (!checkInStatus) return 'morning';

    const currentTime = new Date();
    const timePeriod = getTimePeriod(currentTime, timezonePreferences);
    
    // If both are complete, show completion message
    if (checkInStatus.completionStatus === 'both') {
      return 'complete';
    }

    // Morning logic
    if (timePeriod === 'morning' || timePeriod === 'afternoon') {
      // Morning time: prioritize morning check-in if not done
      if (!checkInStatus.morning) {
        return 'morning';
      }
      // Morning done, but still morning/afternoon time - allow evening if needed
      if (!checkInStatus.evening) {
        return 'evening';
      }
    }

    // Evening logic
    if (timePeriod === 'evening' || timePeriod === 'night') {
      // Evening time: prioritize evening check-in if not done
      if (!checkInStatus.evening) {
        return 'evening';
      }
      // Evening done, but maybe morning still needed
      if (!checkInStatus.morning) {
        return 'morning';
      }
    }

    // Default fallback
    if (!checkInStatus.morning) return 'morning';
    if (!checkInStatus.evening) return 'evening';
    
    return 'complete';
  };

  const handleCheckInComplete = () => {
    // Refresh the status and show completion
    window.location.reload(); // Simple refresh to update status
  };

  const handleManualSelection = (type: 'morning' | 'evening') => {
    setCurrentCheckIn(type);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading your check-in status...</p>
        </div>
      </div>
    );
  }

  // Show the selected check-in component
  if (currentCheckIn === 'morning') {
    return <MorningCheckIn onSuccess={handleCheckInComplete} onCancel={() => setCurrentCheckIn(null)} />;
  }

  if (currentCheckIn === 'evening') {
    return <EveningCheckIn onSuccess={handleCheckInComplete} onCancel={() => setCurrentCheckIn(null)} />;
  }

  const recommendedCheckIn = getRecommendedCheckIn();
  const currentTime = new Date();
  const timePeriod = getTimePeriod(currentTime, timezonePreferences);

  // Completion state
  if (recommendedCheckIn === 'complete') {
    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center space-y-6">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-full p-4">
                <span className="text-3xl">✅</span>
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900">All Done for Today!</h1>
            <p className="text-gray-600">
              You've completed both your morning and evening check-ins. Great job staying consistent!
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Morning Check-in</span>
                <span className="text-green-600 font-semibold">✓ Complete</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Evening Check-in</span>
                <span className="text-green-600 font-semibold">✓ Complete</span>
              </div>
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Check-in selection state
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full p-3">
                <span className="text-2xl">🎯</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Daily Check-in</h1>
            <p className="text-gray-600 text-sm">
              {timePeriod === 'morning' && 'Good morning! Time to set your intentions.'}
              {timePeriod === 'afternoon' && 'Good afternoon! Ready for your check-in?'}
              {timePeriod === 'evening' && 'Good evening! Time to reflect on your day.'}
              {timePeriod === 'night' && 'Good evening! Time to wrap up your day.'}
            </p>
          </div>

          {/* Today's Status */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-800 text-center">Today's Progress</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Morning Check-in</span>
                {checkInStatus?.morning ? (
                  <span className="text-green-600 font-semibold">✓ Complete</span>
                ) : (
                  <span className="text-gray-400">○ Pending</span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Evening Check-in</span>
                {checkInStatus?.evening ? (
                  <span className="text-green-600 font-semibold">✓ Complete</span>
                ) : (
                  <span className="text-gray-400">○ Pending</span>
                )}
              </div>
            </div>
          </div>

          {/* Recommended Check-in */}
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="font-semibold text-gray-800 mb-2">Recommended</h3>
              <button
                onClick={() => handleManualSelection(recommendedCheckIn as 'morning' | 'evening')}
                className={`w-full px-6 py-4 rounded-lg font-semibold text-white transition-all duration-200 shadow-lg ${
                  recommendedCheckIn === 'morning'
                    ? 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600'
                }`}
              >
                {recommendedCheckIn === 'morning' ? (
                  <div className="flex items-center justify-center space-x-2">
                    <span>🌅</span>
                    <span>Start Morning Check-in</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <span>🌙</span>
                    <span>Start Evening Check-in</span>
                  </div>
                )}
              </button>
            </div>

            {/* Alternative Option */}
            {checkInStatus && (checkInStatus.morning || checkInStatus.evening) && recommendedCheckIn !== 'complete' && (
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">Or choose:</p>
                <button
                  onClick={() => handleManualSelection(recommendedCheckIn === 'morning' ? 'evening' : 'morning')}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {recommendedCheckIn === 'morning' ? (
                    <div className="flex items-center justify-center space-x-2">
                      <span>🌙</span>
                      <span>Evening Check-in</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>🌅</span>
                      <span>Morning Check-in</span>
                    </div>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Skip for now */}
          <div className="text-center pt-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 text-sm hover:text-gray-700 transition-colors"
            >
              Skip for now and return to dashboard
            </button>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default CheckInPrompt;